// tenant/SignupPages/TenantLoginPage.tsx
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTenantAuth } from '../../context/tenantAuth';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerBlock: React.CSSProperties = {
  marginBottom: 20,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
  marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  marginBottom: 4,
  opacity: 0.9,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.14)',
  backgroundColor: 'rgba(10, 4, 20, 0.9)',
  color: '#F5F5FA',
  fontSize: 14,
  boxSizing: 'border-box',
  outline: 'none',
} as const;

const inputWrapper: React.CSSProperties = {
  marginBottom: 14,
};

const helperRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 4,
};

const subtleLink: React.CSSProperties = {
  fontSize: 12,
  opacity: 0.75,
  textDecoration: 'underline',
  cursor: 'pointer',
};

const pillButton: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 999,
  backgroundColor: '#7B1FA2',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontSize: 15,
  fontWeight: 500,
  marginTop: 6,
};

const pillButtonDisabled: React.CSSProperties = {
  ...pillButton,
  opacity: 0.6,
  cursor: 'default',
};

const errorBox: React.CSSProperties = {
  fontSize: 12,
  color: '#FFB3C1',
  backgroundColor: 'rgba(255, 82, 82, 0.12)',
  borderRadius: 8,
  padding: '8px 10px',
  marginTop: 4,
  marginBottom: 4,
  border: '1px solid rgba(255,82,82,0.25)',
};

const smallCaption: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.65,
  marginTop: 12,
  textAlign: 'center',
};

const TenantLoginPage: React.FC = () => {
  const history = useHistory();
  const { login, loading, isLoggedIn } = useTenantAuth();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  // Optional: if already logged in, go straight to home
  React.useEffect(() => {
    if (isLoggedIn) {
      history.replace('/tenant/home');
    }
  }, [isLoggedIn, history]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await login(email.trim().toLowerCase(), password);
      history.push('/tenant/home');
    } catch (err: any) {
      console.error('Tenant login error:', err);
      setError(err.message || 'Failed to sign in');
    }
  };

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerBlock}>
          <h1 style={titleStyle}>Log in to your storage</h1>
          <p style={subtitleStyle}>
            Access your appointments, items in storage, and deliveries.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={inputWrapper}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={inputStyle}
              placeholder="you@example.com"
            />
          </div>

          <div style={inputWrapper}>
            <label style={labelStyle}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={inputStyle}
              placeholder="••••••••"
            />
            <div style={helperRow}>
              <span style={{ fontSize: 12, opacity: 0.7 }}>
                Use the same email you used to sign up.
              </span>
              <span
                style={subtleLink}
                onClick={e => {
                  e.preventDefault();
                  history.push('/tenant/password-reset');
                }}
              >
                Forgot password?
              </span>
            </div>
          </div>

          {error && <div style={errorBox}>{error}</div>}

          <button
            type="submit"
            disabled={loading}
            style={loading ? pillButtonDisabled : pillButton}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>

          <div style={smallCaption}>
            Don&apos;t have an account yet? Start by scheduling your first
            pickup on the website.
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantLoginPage;
